import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import './datatable.css';

/* ═══════════════════════════════════════════════════════════════
   Dropdown — Componente genérico de seleção
   Herda o design system do datatable.css (.form-select, .form-input)
   
   Variantes:
     - default: select simples (substitui <select>)
     - searchable: com campo de busca
     - multi: seleção múltipla com badges
   
   Uso:
     <Dropdown
       options={[{ value: 'a', label: 'Opção A' }]}
       value={selectedValue}
       onChange={setValue}
       placeholder="Selecione..."
     />

     <Dropdown
       options={options}
       value={multiValues}
       onChange={setMultiValues}
       multi
       searchable
       placeholder="Escolha vários..."
     />
   ═══════════════════════════════════════════════════════════════ */

export interface DropdownOption {
  value: string;
  label: string;
  icon?: ReactNode;
  color?: string;
  disabled?: boolean;
  group?: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  /** Valor selecionado (string para single, string[] para multi) */
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  placeholder?: string;
  /** Permite busca dentro das opções */
  searchable?: boolean;
  /** Seleção múltipla */
  multi?: boolean;
  /** Desabilitado */
  disabled?: boolean;
  /** Tamanho */
  size?: 'sm' | 'md' | 'lg';
  /** Classe extra no container */
  className?: string;
  /** Largura */
  width?: string | number;
  /** Direção de abertura */
  align?: 'left' | 'right';
  /** Limpar seleção */
  clearable?: boolean;
  /** Label acima (como form-group) */
  label?: string;
  /** Erro de validação */
  error?: string;
  /** Ícone no trigger */
  triggerIcon?: ReactNode;
  /** Render customizado para cada opção */
  renderOption?: (option: DropdownOption, selected: boolean) => ReactNode;
  /** Max items visíveis antes de scroll */
  maxVisible?: number;
}

export function Dropdown({
  options,
  value,
  onChange,
  placeholder = 'Selecione...',
  searchable = false,
  multi = false,
  disabled = false,
  size = 'md',
  className = '',
  width,
  align = 'left',
  clearable = false,
  label,
  error,
  triggerIcon,
  renderOption,
  maxVisible = 7,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Normalizar value
  const selectedValues: string[] = multi
    ? (Array.isArray(value) ? value : value ? [value] : [])
    : (value && !Array.isArray(value) ? [value] : []);

  // Fechar ao clicar fora
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus no search ao abrir
  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [isOpen, searchable]);

  // Fechar com ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setIsOpen(false); setSearch(''); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Filtrar opções
  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Agrupar opções
  const groups = new Map<string, DropdownOption[]>();
  for (const opt of filtered) {
    const g = opt.group || '';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(opt);
  }

  const handleSelect = (optValue: string) => {
    if (multi) {
      const next = selectedValues.includes(optValue)
        ? selectedValues.filter(v => v !== optValue)
        : [...selectedValues, optValue];
      onChange?.(next);
    } else {
      onChange?.(optValue);
      setIsOpen(false);
      setSearch('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(multi ? [] : '');
  };

  const handleRemoveTag = (val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = selectedValues.filter(v => v !== val);
    onChange?.(next);
  };

  // Labels dos selecionados
  const selectedLabels = selectedValues.map(v => options.find(o => o.value === v));

  const sizeClass = size === 'sm' ? 'dd-sm' : size === 'lg' ? 'dd-lg' : '';
  const itemHeight = size === 'sm' ? 28 : size === 'lg' ? 40 : 34;

  return (
    <div
      className={`dd-wrapper ${className} ${sizeClass} ${error ? 'dd-error' : ''}`}
      ref={containerRef}
      style={{ width }}
    >
      {label && <label className="form-label">{label}</label>}

      {/* ── Trigger ── */}
      <button
        type="button"
        className={`dd-trigger${isOpen ? ' dd-trigger-open' : ''}${disabled ? ' dd-trigger-disabled' : ''}`}
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
      >
        <div className="dd-trigger-content">
          {triggerIcon && <span className="dd-trigger-icon">{triggerIcon}</span>}

          {multi && selectedValues.length > 0 ? (
            <div className="dd-tags">
              {selectedLabels.slice(0, 3).map((opt, i) => opt && (
                <span key={opt.value} className="dd-tag">
                  {opt.color && <span className="dd-tag-dot" style={{ background: opt.color }} />}
                  {opt.label}
                  <button className="dd-tag-remove" onClick={(e) => handleRemoveTag(opt.value, e)}>
                    <X size={10} />
                  </button>
                </span>
              ))}
              {selectedValues.length > 3 && (
                <span className="dd-tag dd-tag-more">+{selectedValues.length - 3}</span>
              )}
            </div>
          ) : selectedValues.length === 1 && selectedLabels[0] ? (
            <span className="dd-selected-label">
              {selectedLabels[0].icon && <span className="dd-selected-icon">{selectedLabels[0].icon}</span>}
              {selectedLabels[0].color && <span className="dd-tag-dot" style={{ background: selectedLabels[0].color }} />}
              {selectedLabels[0].label}
            </span>
          ) : (
            <span className="dd-placeholder">{placeholder}</span>
          )}
        </div>

        <div className="dd-trigger-actions">
          {clearable && selectedValues.length > 0 && (
            <span className="dd-clear" onClick={handleClear} title="Limpar">
              <X size={13} />
            </span>
          )}
          <ChevronDown size={14} className={`dd-chevron${isOpen ? ' dd-chevron-open' : ''}`} />
        </div>
      </button>

      {error && <span className="form-error-text">{error}</span>}

      {/* ── Dropdown Panel ── */}
      {isOpen && (
        <div className={`dd-panel dd-align-${align}`}>
          {searchable && (
            <div className="dd-search-wrap">
              <Search size={13} className="dd-search-icon" />
              <input
                ref={searchRef}
                className="dd-search-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar..."
              />
              {search && (
                <button className="dd-search-clear" onClick={() => setSearch('')}>
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          <div className="dd-options custom-scroll" style={{ maxHeight: maxVisible * itemHeight }}>
            {filtered.length === 0 ? (
              <div className="dd-empty">Nenhum resultado</div>
            ) : (
              Array.from(groups.entries()).map(([group, opts]) => (
                <div key={group}>
                  {group && <div className="dd-group-label">{group}</div>}
                  {opts.map(opt => {
                    const selected = selectedValues.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        className={`dd-option${selected ? ' dd-option-selected' : ''}${opt.disabled ? ' dd-option-disabled' : ''}`}
                        onClick={() => !opt.disabled && handleSelect(opt.value)}
                        disabled={opt.disabled}
                      >
                        {renderOption ? renderOption(opt, selected) : (
                          <>
                            {multi && (
                              <span className={`dd-check${selected ? ' dd-check-on' : ''}`}>
                                {selected && <Check size={11} />}
                              </span>
                            )}
                            {opt.icon && <span className="dd-option-icon">{opt.icon}</span>}
                            {opt.color && <span className="dd-tag-dot" style={{ background: opt.color }} />}
                            <span className="dd-option-label">{opt.label}</span>
                            {!multi && selected && <Check size={13} className="dd-option-check" />}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
